/**
 * Predictive / instant search dropdown
 * Uses Shopify Predictive Search API (/search/suggest.json)
 * Shows products + collections, triggers at 3+ chars with 300ms debounce.
 */
(function () {
  'use strict';

  var DEBOUNCE_MS  = 300;
  var MIN_CHARS    = 3;
  var MAX_RESULTS  = 5;

  /* ── helpers ─────────────────────────────────────────────── */

  function esc(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function safeUrl(url) {
    if (typeof url === 'string' && /^\/[^/]/.test(url)) return url;
    return '#';
  }

  function highlight(rawText, query) {
    var safe = esc(rawText);
    var pattern = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return safe.replace(new RegExp('(' + pattern + ')', 'gi'), '<mark>$1</mark>');
  }

  function formatMoney(price) {
    // Shopify Predictive Search API returns price in the currency's major unit
    var amount  = parseFloat(price).toFixed(2);
    var rounded = String(Math.round(parseFloat(price)));
    if (window.money_format_type) {
      // Normalise {{ amount }} / {{amount}} / {{ amount_no_decimals }} etc.
      return window.money_format_type
        .replace(/\{\{\s*amount\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, rounded)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, rounded);
    }
    return '$' + amount;
  }

  function numericPrice(value) {
    if (value === null || value === '' || typeof value === 'undefined') return NaN;
    return Number(value);
  }

  function comparisonPrice(product) {
    var price = numericPrice(product.price);
    var minimum = numericPrice(product.price_min);
    var maximum = numericPrice(product.price_max);
    var compareMin = numericPrice(product.compare_at_price_min);
    var compareMax = numericPrice(product.compare_at_price_max);
    // Aggregate ranges cannot identify which variant owns each original price.
    // Show a comparison only when every current/original price is the same.
    return Number.isFinite(price) && price === minimum && price === maximum &&
      Number.isFinite(compareMin) && compareMin === compareMax && compareMin > price
      ? compareMin : null;
  }

  /* ── main ────────────────────────────────────────────────── */

  function init() {
    document.querySelectorAll('.search-bar[data-insta-search]').forEach(setupSearch);
  }

  function setupSearch(form) {
    var input = form.querySelector('.input-group-field');
    if (!input) return;

    /* Dropdown appended to body to escape overflow:hidden on .nav-search */
    var dropdown = document.createElement('div');
    dropdown.className = 'insta-search-dropdown';
    dropdown.setAttribute('role', 'listbox');
    dropdown.setAttribute('aria-label', 'Search suggestions');
    dropdown.setAttribute('aria-live', 'polite');
    dropdown.hidden = true;
    document.body.appendChild(dropdown);

    var timer       = null;
    var lastQuery   = '';
    var generation  = 0;
    var controller  = null;   // AbortController for in-flight requests

    /* ── positioning ── */
    function position() {
      var r = input.getBoundingClientRect();
      dropdown.style.left  = (r.left + window.scrollX) + 'px';
      dropdown.style.top   = (r.bottom + window.scrollY) + 'px';
      dropdown.style.width = r.width + 'px';
      var viewport = window.visualViewport;
      var bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight;
      dropdown.style.maxHeight = Math.max(0, bottom - r.bottom - 8) + 'px';
    }

    function show() { position(); dropdown.hidden = false; }
    function cancelPending() {
      clearTimeout(timer);
      timer = null;
      generation++;
      if (controller) { controller.abort(); controller = null; }
    }
    function hide() {
      cancelPending();
      dropdown.hidden = true;
      dropdown.innerHTML = '';
      lastQuery = '';
    }

    /* ── input handling ── */
    input.addEventListener('input', function () {
      cancelPending();
      lastQuery = '';
      var q = input.value.trim();
      if (q.length < MIN_CHARS) { hide(); return; }
      var requestGeneration = generation;
      timer = setTimeout(function () { fetchResults(q, requestGeneration); }, DEBOUNCE_MS);
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { hide(); input.blur(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        var first = dropdown.querySelector('.insta-search-item, .insta-search-view-all');
        if (first) first.focus();
      }
    });

    /* Close on outside click */
    document.addEventListener('click', function (e) {
      if (!form.contains(e.target) && !dropdown.contains(e.target)) hide();
    });

    /* Reposition on scroll / resize */
    window.addEventListener('scroll', function () { if (!dropdown.hidden) position(); }, { passive: true });
    window.addEventListener('resize', function () { if (!dropdown.hidden) position(); }, { passive: true });
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () { if (!dropdown.hidden) position(); }, { passive: true });
      window.visualViewport.addEventListener('scroll', function () { if (!dropdown.hidden) position(); }, { passive: true });
    }

    /* ── fetch ── */
    function fetchResults(q, requestGeneration) {
      if (requestGeneration !== generation || q === lastQuery) return;
      lastQuery = q;

      if (controller) controller.abort();
      controller = new AbortController();

      var url = '/search/suggest.json'
        + '?q=' + encodeURIComponent(q)
        + '&resources[type]=product,collection'
        + '&resources[limit]=' + MAX_RESULTS
        + '&resources[options][unavailable_products]=last';

      fetch(url, { signal: controller.signal })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (requestGeneration !== generation || input.value.trim() !== q) return;   // stale response
          render(data.resources.results, q);
        })
        .catch(function (err) {
          if (requestGeneration === generation && err.name !== 'AbortError') hide();
        });
    }

    /* ── render ── */
    function render(results, q) {
      var products    = results.products    || [];
      var collections = results.collections || [];

      if (!products.length && !collections.length) { hide(); return; }

      var html = '';

      if (products.length) {
        html += '<div class="insta-search-section">'
             +  '<div class="insta-search-section-title">Products</div>';
        products.forEach(function (p) {
          var imgHtml = (p.featured_image && p.featured_image.url)
            ? '<img src="' + esc(p.featured_image.url) + '&width=80" alt="" width="40" height="40" loading="lazy">'
            : '<div class="insta-search-no-img"></div>';
          var priceHtml = Number.isFinite(numericPrice(p.price))
            ? '<span class="insta-search-price">' + formatMoney(p.price) + '</span>'
            : '';
          var comparePrice = comparisonPrice(p);
          var compareHtml = comparePrice !== null
            ? '<s class="insta-search-compare">' + formatMoney(comparePrice) + '</s>'
            : '';
          html += '<a class="insta-search-item" href="' + esc(safeUrl(p.url)) + '" role="option" tabindex="-1">'
               +    '<div class="insta-search-thumb">' + imgHtml + '</div>'
               +    '<div class="insta-search-info">'
               +      '<div class="insta-search-title">' + highlight(p.title, q) + '</div>'
               +      '<div class="insta-search-prices">' + priceHtml + compareHtml + '</div>'
               +    '</div>'
               + '</a>';
        });
        html += '</div>';
      }

      if (collections.length) {
        html += '<div class="insta-search-section">'
             +  '<div class="insta-search-section-title">Collections</div>';
        collections.forEach(function (c) {
          html += '<a class="insta-search-item insta-search-item--collection" href="' + esc(safeUrl(c.url)) + '" role="option" tabindex="-1">'
               +    '<div class="insta-search-info">'
               +      '<div class="insta-search-title">' + highlight(c.title, q) + '</div>'
               +      '<div class="insta-search-prices insta-search-coll-label">Collection</div>'
               +    '</div>'
               + '</a>';
        });
        html += '</div>';
      }

      html += '<a class="insta-search-view-all" href="/search?q=' + encodeURIComponent(q) + '&type=product" tabindex="-1">'
           +    'View all results &rarr;'
           + '</a>';

      dropdown.innerHTML = html;
      show();

      /* keyboard navigation within the dropdown */
      var items = Array.prototype.slice.call(
        dropdown.querySelectorAll('.insta-search-item, .insta-search-view-all')
      );
      items.forEach(function (item, idx) {
        item.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowDown') {
            e.preventDefault();
            var next = items[idx + 1];
            if (next) next.focus(); else items[0].focus();
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (idx === 0) { input.focus(); } else { items[idx - 1].focus(); }
          } else if (e.key === 'Escape') {
            hide();
            input.focus();
          }
        });
      });
    }
  }

  /* ── boot ── */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
